<script setup lang="ts">
import { useChromeStore } from "~/composables/stores/chrome";

const props = defineProps({
  panelFocusBlockAll: {
    type: Boolean,
    default: false,
  },
});

const chromeStore = useChromeStore();

const { blockUI } = storeToRefs(chromeStore);
const tooltipChromeBottom = useTemplateRef("tooltipChromeBottom");

const hideTooltip = () => {
  tooltipChromeBottom.value?.classList.remove("tooltip-chrome-bottom-active");
};

const tooltipClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement;

  if (!target?.matches("abbr")) hideTooltip();
};

const shouldBlockScroll = (e: Event) => {
  hideTooltip();

  if (props.panelFocusBlockAll || blockUI.value) {
    e.preventDefault();
  }
};

useClickOutside(tooltipChromeBottom, tooltipClickOutside);
</script>

<template>
  <div style="display: flex; flex-direction: column; height: 100vh; user-select: text">
    <div :class="['chrome-container', $style.container]">
      <div :class="['chrome-container-aside', $style.containerAside]">
        <slot name="sideMenu" />
      </div>

      <div :class="['chrome-container-content', $style.containerContent]" :style="{ width: `100%` }">
        <div
          :class="[
            'container-content-bottom-overflow-container',
            $style.containerContentBottomOverflowContainer,
            (panelFocusBlockAll || blockUI) && $style.overflowDisable,
          ]"
          @wheel="shouldBlockScroll"
          @touchmove="shouldBlockScroll"
          @scroll="shouldBlockScroll"
        >
          <slot name="content" />
        </div>
      </div>

      <Overlay
        v-if="panelFocusBlockAll || blockUI"
        colorForeground="--color-primary"
        colorBackground="--color-accent-200"
        opacityBackground="0.1"
        opacityForeground="0.4"
        :opacityEffectDisable="panelFocusBlockAll || blockUI"
        calculateHeightByParent
        maskImageDisable
      />

      <!--
      TODO: cllpse:
      Investigate weird portal bug where you can't have nested portals in-between components.
      Aditional portals named "chrome-notification-layer{n}" will have to do for now.
    -->
      <div :class="[$style.portalFloatingAction, blockUI && $style.notificationActive]">
        <div :class="['portal-floating-actionLayers', $style.portalFloatingActionLayers]">
          <div v-for="modal in chromeStore.registeredModals" :key="modal" :class="['portal-floating-action-layer', $style.portalFloatingActionLayer]">
            <portal-target :name="`chrome-notification-layer-${modal}`" />
          </div>

          <div style="display: flex; z-index: -1; height: 999999vh; overflow: hidden; opacity: 0; visibility: none" />
        </div>
      </div>

      <div id="tooltip-chrome-bottom" ref="tooltipChromeBottom" class="tooltip-chrome-bottom">
        <div id="tooltip-chrome-bottom-close">
          <Icon id="close" size="medium" color="--color-primary" />
        </div>

        <div id="tooltip-chrome-bottom-content" />
      </div>
    </div>
  </div>
</template>

<style module>
.container {
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.containerAside {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 16rem;
  background-color: var(--color-primary);
}

.containerContent {
  display: flex;
  flex-direction: column;
  overflow: auto;
  width: 100%;
  height: 100%;
}

.containerContentBottom {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.containerContentBottom.overflowDisable {
  overflow: hidden;
}

.containerContentBottomOverflowContainer {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: var(--container-content-start–bottom–overflow-container-color-foreground)
    var(--container-content-start–bottom–overflow-container-color-background);
  scrollbar-width: thin;
}

.containerContentBottomOverflowContainer::-webkit-scrollbar {
  width: 1rem;
  height: 0;
}

.containerContentBottomOverflowContainer::-webkit-scrollbar-track {
  background: var(--container-content-start–bottom–overflow-container-color-background);
}

.containerContentBottomOverflowContainer::-webkit-scrollbar-thumb {
  background-color: var(--container-content-start–bottom–overflow-container-color-foreground);
  border-radius: calc(1rem - var(--border-radius-medium));
  border: 0.25rem solid var(--container-content-start–bottom–overflow-container-color-background);
}

.containerContentBottomOverflowContainer.overflowDisable::-webkit-scrollbar-thumb {
  background-color: transparent;
  border: none;
}

.containerContentBottomOverflowContainer::-webkit-scrollbar-thumb:hover {
  border-width: 0.2rem;
}

.portalFloatingAction {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: fixed;
  height: 0;
  max-height: calc(100vh - 5rem);
  top: 5rem;
  z-index: 999999999;
  pointer-events: none;
}

.portalFloatingAction.notificationActive {
  height: 100%;
  width: 100%;
}

.portalFloatingActionLayers {
  display: flex;
  flex-direction: column;
  position: relative;
  height: inherit;
  pointer-events: none;
}

.portalFloatingActionLayer {
  display: flex;
  flex-direction: column;
  height: inherit;
  pointer-events: all;
  align-items: center;
  cursor: pointer;
}

/* :global(.splitPercentageDragging > *) {
  user-select: none !important;
  outline: solid 2px cyan;
  outline-offset: -2px;
} */

:global(#tooltip-chrome-bottom) {
  display: flex;
  z-index: -999999999;
  flex-direction: column;
  position: absolute;
  width: 20rem;
  padding: 0.75rem 0.75rem;
  opacity: 0;
  box-shadow: var(--box-shadow-large);
  background-color: var(--color-white);
  border: solid 1px var(--color-accent-200);
  color: var(--color-primary);
  transition-property: opacity;
  transition-duration: var(--transition-duration-shortest);
  transition-timing-function: ease-in-out;
}

:global(#tooltip-chrome-bottom.tooltip-chrome-bottom-active) {
  z-index: 999999999;
  opacity: 1;
}

:global(#tooltip-chrome-bottom-content) {
  display: flex;
  flex-direction: column;
  white-space: pre-line;
}

:global(#tooltip-chrome-bottom:not(.tooltip-chrome-bottom-no-close-button) #tooltip-chrome-bottom-content) {
  padding-right: 1rem;
}

:global(#tooltip-chrome-bottom-close) {
  display: none;
  justify-content: flex-end;
  position: absolute;
  right: 1rem;
  top: 1.125rem; /* @cllpse: adjusting for visual height; align with typography */
  cursor: pointer;
}

:global(#tooltip-chrome-bottom:not(.tooltip-chrome-bottom-no-close-button) #tooltip-chrome-bottom-close) {
  display: flex;
}
</style>
